import React, { useState, useRef, useEffect } from "react";
import moment from "moment-timezone";
import { TriangleAlertIcon, ChevronDown, ChevronRight } from "lucide-react";
import {
  useFormV2,
  FormField,
  FormGrid,
  FormActions,
} from "@/lib/form-v2";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TradingAccount } from "@/lib/api/otc";
import { Customer } from "@/lib/api/reports";
import { getActiveValidator } from "@/lib/form-v2";
import { useListTraders } from "@/lib/api/trader";
import { getOptionMonthComboBase } from "@/lib/api/common";
import { Trading } from "lhc_common";
import { UpdateContractInfo } from "../OtcBearState";
import { OtcFormValues } from "./types";
import { otcSchemaConfig } from "./schema-config";
import { buildFieldMeta } from "./field-meta";
import { formToApi } from "./transform";
import {
  isReownContractType,
  parseContractRefGroups,
  serializeContractRefGroups,
  stripContractRefDelimiters,
  validateContractRefGroups,
  ContractRefGroup,
} from "./contract-reference-utils";
import { ReownContractReferenceEditor } from "./ReownContractReferenceEditor";

// ---------------------------------------------------------------------------
// Expiration warning helper
// ---------------------------------------------------------------------------

function useExpirationWarning(values: OtcFormValues) {
  let differs = false;
  let expectedDate: Date | undefined;

  if (values.option_month && values.end_date) {
    try {
      const optResult = getOptionMonthComboBase(values.option_month);
      const computed = Trading.Dates.Expiration.calculateLastTradingDay(
        optResult.firstDayOfMonthMoment,
      );
      expectedDate = computed;
      if (computed) {
        differs = !moment(values.end_date as Date)
          .endOf("day")
          .isSame(moment(computed).endOf("day"));
      }
    } catch {
      // ignore
    }
  }

  return { differs, expectedDate };
}

// ---------------------------------------------------------------------------
// Adjusted-row collapse helpers
// ---------------------------------------------------------------------------

/** Fields that indicate user-entered data for each adjusted row (excludes derived fields). */
const ADJ_ROW_DATA_FIELDS: string[][] = [
  ["adj_option_premium", "adj_contract_fee", "last_adjusted_date"],
  ["adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two"],
  ["adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three"],
];

/**
 * Returns the highest 1-indexed adjusted row that contains data.
 * 0 means no adjusted row has data.
 */
function highestAdjRowWithData(values: OtcFormValues): number {
  let highest = 0;
  for (let i = 0; i < ADJ_ROW_DATA_FIELDS.length; i++) {
    const hasData = ADJ_ROW_DATA_FIELDS[i].some((key) => {
      const v = values[key];
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    });
    if (hasData) highest = i + 1;
  }
  return highest;
}

/**
 * Build a label like "Adj Row 3 (No Data)" or "Adj Row 2 & 3 (No Data)"
 * for the collapsed rows.
 */
function collapsedAdjLabel(hiddenRowNumbers: number[]): string {
  if (hiddenRowNumbers.length === 0) return "";
  const nums = hiddenRowNumbers.join(" & ");
  return `Adj Row ${nums} (No Data)`;
}

// ---------------------------------------------------------------------------
// OtcContractFormV2
// ---------------------------------------------------------------------------

export function OtcContractFormV2({
  isPreviewOnly,
  accounts,
  customers,
  isUpdateForm,
  initialValues,
  onSubmit,
}: {
  isPreviewOnly?: boolean;
  accounts: TradingAccount[];
  customers: Customer[];
  isUpdateForm: boolean;
  initialValues: OtcFormValues;
  onSubmit: (formData: UpdateContractInfo) => Promise<boolean>;
}) {
  const listTradersRes = useListTraders();
  const traders = listTradersRes?.data?.data || [];

  const fieldMeta = buildFieldMeta({
    accounts,
    customers,
    traders,
    isPreviewOnly,
  });

  const form = useFormV2<OtcFormValues>(
    otcSchemaConfig,
    fieldMeta,
    initialValues,
  );

  const { differs: expirationDiffers, expectedDate } = useExpirationWarning(form.values);

  // Adjusted rows: compute which rows have data and default-expand accordingly
  // Not on initial values but always latest so the user can always hide and be aware
  const highestWithData = highestAdjRowWithData(form.values as any);
  const [adjRowsExpanded, setAdjRowsExpanded] = useState(false);

  // Reown contract reference multi-group state
  const [refGroups, setRefGroups] = useState<ContractRefGroup[]>(() =>
    parseContractRefGroups(initialValues.contract_reference),
  );

  // Reown contract reference helpers
  const isReown = isReownContractType(form.values.contract_type as string | undefined);
  const wasReownRef = useRef(isReown);

  // When switching away from a reown type, strip the delimiters from
  // contract_reference so the plain text field gets a clean value.
  useEffect(() => {
    if (wasReownRef.current && !isReown) {
      const plain = stripContractRefDelimiters(form.values.contract_reference);
      form.setFieldValue("contract_reference", plain || undefined);
      setRefGroups([{ ref: "", qty: "" }]);
    }
    wasReownRef.current = isReown;
  }, [isReown]);

  if ([listTradersRes].some((r) => r.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  // ---- Submit ----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreviewOnly) return;

    form.attemptSubmit();
    const result = form.validate();
    if (!result.success) return;

    const validator = getActiveValidator(otcSchemaConfig, form.values.contract_type as string | undefined);
    const apiData = formToApi(form.values, validator);
    await onSubmit(apiData as unknown as UpdateContractInfo);
  };

  // ---- Render helpers ----

  const renderField = (name: string, extra?: React.ReactNode) => {
    const props = form.getFieldProps(name);
    if (!props || !props.visible) return null;

    return (
      <FormField
        key={name}
        fieldProps={props}
        onChange={(value) => form.setFieldValue(name, value)}
        onBlur={() => form.touchField(name)}
        extra={extra}
      />
    );
  };

  const isVisible = (name: string): boolean => {
    const props = form.getFieldProps(name);
    return !!props?.visible;
  };

  const handleRefGroupsChange = (groups: ContractRefGroup[]) => {
    setRefGroups(groups);
    const serialized = serializeContractRefGroups(groups);
    form.setFieldValue("contract_reference", serialized || undefined);
  };

  // Expiration date extra tooltip
  const expirationExtra = expirationDiffers ? (
    <Tooltip>
      <TooltipTrigger>
        <TriangleAlertIcon className="h-4 w-4 text-orange-400" />
      </TooltipTrigger>
      <TooltipContent>
        <p>Expected an end date of {moment(expectedDate).format("MM/DD/YYYY")}</p>
      </TooltipContent>
    </Tooltip>
  ) : null;

  return (
    <form aria-disabled={isPreviewOnly} onSubmit={handleSubmit}>
      <div className="flex flex-col md:flex md:flex-col items-start gap-4 content-center">
        {/* Row 1 */}
        <FormGrid cols={5}>
          {renderField("date")}
          {renderField("end_date", expirationExtra)}
          {renderField("contract_type")}
          {renderField("customer_id")}
          {renderField("commodity")}
        </FormGrid>

        {/* Row 2 */}
        <FormGrid cols={5}>
          {renderField("option_month")}
          {renderField("quantity")}
          {renderField("trade_reference")}
          {renderField("account_no_a")}
        </FormGrid>

        {/* Steps row – only visible fields render */}
        <FormGrid cols={6}>
          {renderField("step_one")}
          {renderField("step_two")}
          {renderField("step_three")}
          {renderField("double_up")}
          {renderField("implied_fx_amount")}
        </FormGrid>

        {/* Canola min price row */}
        {isVisible("expected_contract_futures") && (
          <FormGrid cols={3}>
            {renderField("expected_contract_futures")}
            {renderField("actual_contract_futures")}
            {renderField("futures_margin")}
          </FormGrid>
        )}

        {/* Premium Offer WDU row */}
        {isVisible("account_no_b") && (
          <FormGrid cols={5}>
            {renderField("account_no_b")}
            {renderField("futures_price")}
            {renderField("otc_futures")}
          </FormGrid>
        )}

        {/* Financials */}
        <FormGrid cols={6}>
          {renderField("option_premium")}
          {renderField("contract_fee")}
          {renderField("margin")}
          {renderField("margin_amount")}
          {renderField("contract_no")}
          {!isReown && renderField("contract_reference")}
        </FormGrid>

        {/* Reown contract references (multi-group) */}
        {isReown && isVisible("contract_reference") && (
          <ReownContractReferenceEditor
            groups={refGroups}
            onChange={handleRefGroupsChange}
            disabled={isPreviewOnly}
            errors={validateContractRefGroups(refGroups)}
          />
        )}

        {/* Collapsed adjusted rows toggle */}
        {(() => {
          const hiddenRows: number[] = [];
          if (highestWithData < 1 && !adjRowsExpanded) hiddenRows.push(1);
          if (highestWithData < 2 && !adjRowsExpanded) hiddenRows.push(2);
          if (highestWithData < 3 && !adjRowsExpanded) hiddenRows.push(3);

          if (hiddenRows.length === 0 && !adjRowsExpanded) return null;

          return (
            <button
              type="button"
              data-testid="adj-rows-toggle"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-bold"
              onClick={() => setAdjRowsExpanded((prev) => !prev)}
            >
              {adjRowsExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {adjRowsExpanded
                ? "Collapse adjusted rows"
                : collapsedAdjLabel(hiddenRows)}
            </button>
          );
        })()}

        {/* Adjusted 1 */}
        {(highestWithData >= 1 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium")}
            {renderField("adj_contract_fee")}
            {renderField("adj_margin")}
            {renderField("adj_margin_amount")}
            {renderField("last_adjusted_date")}
          </FormGrid>
        )}

        {/* Adjusted 2 */}
        {(highestWithData >= 2 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium_two")}
            {renderField("adj_contract_fee_two")}
            {renderField("adj_margin_two")}
            {renderField("adj_margin_amount_two")}
            {renderField("adjusted_date_two")}
          </FormGrid>
        )}

        {/* Adjusted 3 */}
        {(highestWithData >= 3 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium_three")}
            {renderField("adj_contract_fee_three")}
            {renderField("adj_margin_three")}
            {renderField("adj_margin_amount_three")}
            {renderField("adjusted_date_three")}
          </FormGrid>
        )}

        {/* Accumulator */}
        {isVisible("start_date") && (
          <FormGrid cols={4}>
            {renderField("start_date")}
            {renderField("weeks")}
            {renderField("period_qty")}
            {renderField("knockout")}
          </FormGrid>
        )}

        {/* Shipment / Trader */}
        <FormGrid cols={5}>
          {renderField("shipment_start_date")}
          {renderField("shipment_end_date")}
          {renderField("trader_id")}
          {renderField("crop_year")}
          {renderField("rolled_option_month")}
        </FormGrid>

        {/* Carry / Basis / Notes */}
        <FormGrid cols={5}>
          {renderField("carry")}
          {renderField("rolled_basis_month")}
          {renderField("basis_carry")}
          {renderField("notes")}
          {renderField("option_month_etc_one")}
        </FormGrid>

        {/* Actions */}
        <FormActions>
          <DialogClose asChild>
            <Button type="button" variant="secondary" className="col-start-1">
              Close
            </Button>
          </DialogClose>

          {!isPreviewOnly && (
            <Button
              type="submit"
              size="sm"
              className="px-3 col-start-4"
              disabled={!form.isValid}
              data-testid="form-submit"
            >
              {isUpdateForm ? "Update" : "Create"}
            </Button>
          )}
        </FormActions>
      </div>
    </form>
  );
}
